import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { Flex } from '~/shared/components';
import useDynamicRecoil from '~/shared/hooks/useDynamicRecoil';
import { DateFilterPreset } from '../types';
import { dateValues, dateShortDisplayNames } from '../constants';
import DateFilterRangeButton from './DateFilterRangeButton';

const DEFAULT_RECOIL_KEY = 'dateFilterRange';
const DEFAULT_SELECTED_PRESET = 'Trending 7 Days';

export interface DateFilterRangeButtonsProps {
  presets?: DateFilterPreset[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  className?: string;
}

const rangeButtonCss = css`
  color: #33485e;
  font-size: 14px;
  font-weight: 400;
  border: 1px solid #33485e;
  border-radius: 30px;
  background: transparent;
  width: 40px;
  padding: 7px 15px 6px;
  margin: 16px 12px;
  text-transform: unset !important;
`;

const rangeButtonActiveCss = css`
  color: white !important;
  font-size: 14px;
  font-weight: 400;
  border: 1px solid var(--filter-bar-range-button-active-border) !important;
  border-radius: 30px;
  background: var(--filter-bar-range-button-active-background) !important;
  width: 40px;
  max-width: 40px;
  padding: 7px 15px 6px;
  margin: 16px 12px;
  text-transform: unset !important;
`;

const Wrapper = styled(Flex)`
  width: 100%;
  justify-content: flex-start;
  .range-button {
    ${rangeButtonCss};
    &:hover {
      ${rangeButtonActiveCss};
    }
    &:active {
      ${rangeButtonActiveCss};
    }
  }
  .range-button-selected {
    ${rangeButtonActiveCss};
    &:hover {
      ${rangeButtonActiveCss};
    }
    :active {
      ${rangeButtonActiveCss};
    }
  }
`;

const DEFAULT_PRESETS: DateFilterPreset[] = dateShortDisplayNames
  .map((label, index) => {
    const presetValue = dateValues[index];
    if (!presetValue) return null;
    return { label, value: presetValue };
  })
  .filter(Boolean) as DateFilterPreset[];

export const DEFAULT_DATE_RANGE_PRESETS = DEFAULT_PRESETS;
export const DEFAULT_DATE_RANGE_SELECTION = DEFAULT_SELECTED_PRESET;

const DateFilterRangeButtons = ({
  presets,
  value,
  defaultValue = DEFAULT_SELECTED_PRESET,
  onChange,
  className,
}: DateFilterRangeButtonsProps) => {
  const resolvedPresets = useMemo(() => {
    if (presets && presets.length) {
      return presets;
    }
    return DEFAULT_PRESETS;
  }, [presets]);
  const [selectedRange, setSelectedRange] = useDynamicRecoil(DEFAULT_RECOIL_KEY, defaultValue);

  const isControlled = value !== undefined;
  const activeValue = isControlled ? value : selectedRange;

  const handleSelect = useCallback(
    (nextValue: string) => {
      if (!isControlled) {
        setSelectedRange(nextValue);
      }
      onChange?.(nextValue);
    },
    [isControlled, onChange, setSelectedRange],
  );

  return (
    <Wrapper className={className}>
      {resolvedPresets.map(preset => {
        return (
          <DateFilterRangeButton
            key={preset.value}
            text={preset.label}
            value={preset.value}
            selected={preset.value === activeValue}
            onSelect={handleSelect}
          />
        );
      })}
    </Wrapper>
  );
};

export default DateFilterRangeButtons;
